/**
 * NotificationContext - State management for notifications
 * 
 * Handles:
 * - WebSocket connection lifecycle (connect after auth, disconnect on logout)
 * - Real-time notification updates
 * - Unread count badge
 * - Mark as read functionality
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import { AuthContext } from '../components/AuthContext';

const NotificationContext = createContext();

// WebSocket URL - use wss for production, ws for localhost
const getWebSocketUrl = (token) => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/ws/notifications/?token=${token}`;
};

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (offset = 0, limit = 20, append = false) => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const response = await api.get('/notifications/', {
        params: { offset, limit }
      });
      
      const { results, count } = response.data;
      
      if (append) {
        setNotifications(prev => [...prev, ...results]);
      } else {
        setNotifications(results);
      }
      
      setHasMore(offset + results.length < count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/notifications/unread-count/');
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, []);

  // Mark notifications as read
  const markAsRead = useCallback(async (notificationIds) => {
    if (!notificationIds || notificationIds.length === 0) return;
    
    try {
      const response = await api.post('/notifications/mark-read/', {
        notification_ids: notificationIds
      });
      
      setUnreadCount(response.data.unread_count);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          notificationIds.includes(n.id) ? { ...n, is_read: true } : n
        )
      );
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/notifications/mark-all-read/');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, []);

  // Connect WebSocket
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('access');
    if (!token) {
      console.log('🔔 No token available, skipping WebSocket connection');
      return;
    }
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('🔔 WebSocket already connected');
      return;
    }
    
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log('🔔 WebSocket is connecting...');
      return;
    }

    try {
      const wsUrl = getWebSocketUrl(token);
      console.log('🔔 Connecting to WebSocket:', wsUrl.replace(token, 'TOKEN_HIDDEN'));
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('🔔 Notification WebSocket connected successfully');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🔔 WebSocket message received:', data.type, data);
          
          switch (data.type) {
            case 'notification':
              // New notification received
              console.log('🔔 New notification:', data.notification);
              setNotifications(prev => [data.notification, ...prev]);
              setUnreadCount(prev => prev + 1);
              
              // Optional: Show browser notification if permitted
              if (Notification.permission === 'granted') {
                new Notification(data.notification.message || 'Новое уведомление', {
                  icon: '/wism_favicon.png',
                  body: data.notification.actor?.username || '',
                });
              }
              break;
              
            case 'unread_count':
              console.log('🔔 Unread count updated:', data.unread_count);
              setUnreadCount(data.unread_count);
              break;
              
            case 'marked_read':
            case 'marked_all_read':
              setUnreadCount(data.unread_count);
              break;
              
            default:
              console.log('🔔 Unknown WebSocket message type:', data.type);
          }
        } catch (e) {
          console.error('🔔 Failed to parse WebSocket message:', e, event.data);
        }
      };

      ws.onclose = (event) => {
        console.log('🔔 Notification WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;
        
        // Reconnect logic (only if authenticated and not intentional close)
        if (event.code !== 1000 && event.code !== 4001 && isAuthenticated) {
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            console.log(`🔔 WebSocket reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current++;
              connectWebSocket();
            }, delay);
          } else {
            console.log('🔔 Max reconnection attempts reached');
          }
        }
      };

      ws.onerror = (error) => {
        console.error('🔔 WebSocket error:', error);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('🔔 Failed to create WebSocket:', error);
    }
  }, [isAuthenticated]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Logout');
      wsRef.current = null;
    }
    
    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Connect/disconnect based on auth state
  useEffect(() => {
    if (isAuthenticated) {
      const startupTimer = setTimeout(() => {
        fetchUnreadCount();
        fetchNotifications();
        connectWebSocket();
        if ('Notification' in window && Notification.permission === 'default') {
          Notification.requestPermission();
        }
      }, 2000);
      return () => {
        clearTimeout(startupTimer);
        disconnectWebSocket();
      };
    } else {
      // Cleanup on logout
      disconnectWebSocket();
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load more notifications
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      fetchNotifications(notifications.length, 20, true);
    }
  }, [notifications.length, isLoading, hasMore, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    isConnected,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    loadMore,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export default NotificationContext;

